/** Camera Manager – third-person camera only. Public: createThirdPersonCamera, setTarget, update, getYaw. */

import * as THREE from 'three';
import type { ICameraController, CameraTarget, ThirdPersonCameraConfig } from '../types/CameraTypes';
import { CameraMode } from '../types/CameraTypes';
import { ThirdPersonCamera } from '../cameras/ThirdPersonCamera';

export class CameraManager {
    private cameras: Map<CameraMode, ICameraController> = new Map();
    private activeCamera: ICameraController | null = null;
    private target: CameraTarget | null = null;

    /** Creates and registers third-person camera (orbit behind target, zoom). */
    createThirdPersonCamera(camera: THREE.PerspectiveCamera | THREE.OrthographicCamera, config: ThirdPersonCameraConfig = {}): ThirdPersonCamera {
        const thirdPersonCamera = new ThirdPersonCamera(camera, config);
        this.cameras.set(thirdPersonCamera.mode, thirdPersonCamera);
        if (!this.activeCamera) {
            this.activeCamera = thirdPersonCamera;
            if (this.target) thirdPersonCamera.setTarget(this.target);
        }
        return thirdPersonCamera;
    }

    /** Sets the object all cameras follow (e.g. player). */
    setTarget(target: CameraTarget): void {
        this.target = target;
        this.cameras.forEach(c => c.setTarget(target));
    }

    /** Call each frame with deltaTime (seconds). */
    update(deltaTime: number): void {
        if (this.activeCamera) this.activeCamera.update(deltaTime);
    }

    /** Yaw of active camera in radians (for movement direction). Returns 0 if none. */
    getYaw(): number {
        if (!this.activeCamera) return 0;

        if ('getYaw' in this.activeCamera && typeof this.activeCamera.getYaw === 'function') {
            return this.activeCamera.getYaw();
        }
        // Fallback: use camera rotation.y
        const camera = this.activeCamera.getCamera();
        if (camera instanceof THREE.PerspectiveCamera || camera instanceof THREE.OrthographicCamera) {
            return camera.rotation.y;
        }

        return 0;
    }
}

